import {currentUser} from '@/services/FirestoreService';
import {MapService, gmAPI} from '../../services/MapService';
import {Graphic} from './Graphic';
import {MetricService} from '@/services/MetricService.js';

/**
 * Asynchronously constructs a new DroneGraphic after fetching the api key from Secret Manager
 * and current user metrics from Firestore
 * @async The Nearmaps API key needs to be fetched before creating the custom ImageMapType.
 * @async The user metrics document from Firestore
 * @return {DroneGraphic} The constructed graphic.
 */
async function createDroneGraphic() {
  const key = await _fetchNearMapsKey();
  // fetch the current month's usage from BigQuery
  const currentMonthUsage = await MetricService.getTileUsage('nearmapsTiles');

  return new DroneGraphic({key, currentMonthUsage});
}


/**
 * Fetches the nearmap api key that will be attached to the `getTileUrl` function in the Drone ImageMapType
 * @private
 * @async
 * @type {Function}
 */
const _fetchNearMapsKey = async () => {
  try {
    const idToken = await currentUser().getIdToken(true);
    const urlEnv = process.env.VUE_APP_GCP_ENV === 'production' ? '' : '-staging';
    const response = await fetch(
      `https://us-central1-northpoint-production.cloudfunctions.net/retrieveMapsToken${urlEnv}?retrieve=nearmapsKey`,
      {method: 'GET', headers: {Authorization: `Bearer ${idToken}`}},
    );
    return await (response.json());
  } catch (error) {
    throw new Error(`The Nearmaps API Key could not be fetched, thus the Drone Imagery layer is currently unavailable ${error}`);
  }
};

/**
 * Graphic class for a 'Drone' graphic that conforms to other graphic variants
 * @property {boolean} _render Whether the graphic is currently rendered
 * @property {string} _key Nearmaps API Key that must be passed to the `getTileUrl` function for requesting tiles
 */

class DroneGraphic extends Graphic {
  /**
   * Create a DroneGraphic
   * @param {Object} data
   */
  constructor(data) {
    super();
    this._key = data.key;
    this._render = false;
    this._droneIndex = -1;
    this.currentMonthUsage = data.currentMonthUsage;
    this._cachedSurveys = new Map();
  }

  /**
   * This function will only remove the current ImageMapType if it exists,
   * the creation of this MapType is left up to the SurveyDropdown component
   * @param {google.maps.Map|null} map The map to attach this layer to,
   * or `null` to clear the layer from its current map.
   */
  setMap(map) {
    if (!map) {
      const overlayMapTypes = MapService.getMap().overlayMapTypes;
      overlayMapTypes.forEach((overlayMapType, i) => {
        if (isDrone(overlayMapType)) {
          const droneLayer = overlayMapTypes.removeAt(i);
          if (!this._layer) {
            this._layer = droneLayer;
          }
          this._render = false;
        }
      });
    } else {
      this.createLayer();
    }
  }

  /**
   * @return {Array<Object>} Returns an array of survey objects for a the currently centered map coordinates
   */
  async getSurveys() {
    const {_key} = this;
    const map = MapService.getMap();
    const coords = MapService.getTileCoords();
    // by default the limit is 20, but we can increase it with a query parameter 'limit'.
    // here it is increased to 100.
    const {surveys} = await fetch(
      `https://api.nearmap.com/coverage/v2/coord/${Math.floor(map.zoom)}/${coords.x}/${coords.y}?apikey=${_key}&limit=100`)
      .then((res) => res.json())
      .catch((err) => console.error('error', err));
    return surveys;
  }

  /**
   * This function handles creating new ImageMapTypes for drone layers or updating a layer if it already exists
   * @param {string} survey - The survey ID for the desired imagery to fetch
   */
  createLayer(survey) {
    let droneIndex = -1;
    const overlayMapTypes = MapService.getMap().overlayMapTypes;
    overlayMapTypes?.forEach((overlay, index) => {
      if (isDrone(overlay)) {
        droneIndex = index;
      }
    });

    const apiKey = this._key;
    /**
     * This is a scoped helper function responsible for incrementing both the local metric and
     * usage property for nearmaps tiles
     */
    const incrementUsage = () => {
      MetricService.increaseMetric('nearmapsTiles');
      this.currentMonthUsage++;
    };
    let droneMapType;
    if (this._cachedSurveys.has(survey)) {
      droneMapType = this._cachedSurveys.get(survey);
    } else {
      const newLayer = new gmAPI.ImageMapType({
        getTileUrl: (() => {
          const cache = {};
          return (coord, zoom) => {
            const key = `${coord.x}-${coord.y}-${zoom}`;
            if (cache[key]) return cache[key];
            incrementUsage();
            cache[key] = `https://api.nearmap.com/tiles/v3/${survey ? `surveys/${survey}` : ''}/Vert/${zoom}/${coord.x}/${coord.y}.jpg?apikey=${apiKey}`;
            return cache[key];
          };
        })(), // IIFE to memoize the tile urls
        tileSize: new gmAPI.Size(256, 256),
        name: 'drone',
        minZoom: 1,
        maxZoom: 16,
      });
      this._cachedSurveys.set(survey, newLayer);
      droneMapType = newLayer;
    }
    // Push a new ImageMapType layer if a drone layer hasn't been created yet or replace the existing one
    if (droneIndex < 0) {
      overlayMapTypes.push(droneMapType);
    } else {
      overlayMapTypes.setAt(droneIndex, droneMapType);
    }
    this._render = true;
  }
}

/**
 * Determines whether an overlayMapType is the drone layer.
 * @param {google.maps.MapType} overlayMapType
 * @return {boolean} `true` if it's the drone layer; else false.
 */
function isDrone(overlayMapType) {
  return overlayMapType.name === 'drone';
}

export {createDroneGraphic, DroneGraphic};
